// Tiny DOM helper. Strings always become text nodes, never HTML (spec §7.3).
export type Child = Node | string | number | null | undefined | false | Child[];
type Attrs = Record<string, unknown> & { class?: string; style?: string | Partial<CSSStyleDeclaration> };

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Attrs | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v === undefined || v === null || v === false) continue;
      if (k === 'style' && typeof v === 'object') {
        for (const [sk, sv] of Object.entries(v)) {
          if (sv === undefined || sv === '') continue;
          if (sk.startsWith('--')) el.style.setProperty(sk, String(sv)); else (el.style as any)[sk] = sv;
        }
      }
      else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      else if (k in el && k !== 'list' && k !== 'form' && typeof v !== 'string') (el as any)[k] = v;
      else el.setAttribute(k, v === true ? '' : String(v));
    }
  }
  append(el, children);
  return el;
}

export function append(el: Node, children: Child[]) {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    if (Array.isArray(c)) append(el, c);
    else el.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
}

export function clear(el: Node, ...children: Child[]) {
  while (el.firstChild) el.removeChild(el.firstChild);
  append(el, children);
}

/** Attributes for a Basecoat button: primary unless a variant is given. Spread into h('button', …). */
export function btn(variant?: 'outline' | 'ghost' | 'secondary' | 'destructive' | 'link', size?: 'sm' | 'xs' | 'icon-sm'): Attrs {
  return { class: 'btn', type: 'button', 'data-variant': variant, 'data-size': size };
}

const SVG_NS = 'http://www.w3.org/2000/svg';
/** Small line icon from a path list (24px viewBox, 1.75 stroke). */
export function icon(paths: string[], size = 16): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.75');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  for (const d of paths) {
    const p = document.createElementNS(SVG_NS, 'path');
    p.setAttribute('d', d);
    svg.appendChild(p);
  }
  return svg;
}

export const ICONS = {
  info: ['M12 16v-4', 'M12 8h.01', 'M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z'],
  copy: ['M8 8h12v12H8z', 'M16 8V4H4v12h4'],
  close: ['M18 6 6 18', 'M6 6l12 12'],
  upload: ['M12 16V4', 'M7 9l5-5 5 5', 'M4 20h16'],
  download: ['M12 4v12', 'M7 11l5 5 5-5', 'M4 20h16'],
  sun: ['M12 3v2', 'M12 19v2', 'M5 12H3', 'M21 12h-2', 'M6.3 6.3 4.9 4.9', 'M19.1 19.1l-1.4-1.4', 'M6.3 17.7l-1.4 1.4', 'M19.1 4.9l-1.4 1.4', 'M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0z'],
  moon: ['M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z'],
  monitor: ['M3 4h18v12H3z', 'M8 20h8', 'M12 16v4'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'M20 20l-4-4'],
  chevron: ['M6 9l6 6 6-6'],
  check: ['M20 6 9 17l-5-5'],
  alert: ['M12 9v4', 'M12 17h.01', 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z'],
};

/** A Basecoat alert: an icon, the message and optional footer controls (such as Dismiss).
 *  Destructive ones are errors and are announced (role=alert); the rest are notes. */
export function alert(message: string, variant?: 'destructive', ...footer: Child[]): HTMLElement {
  return h('div', { class: 'alert', role: variant ? 'alert' : 'note', 'data-variant': variant },
    icon(variant ? ICONS.alert : ICONS.info, 16), h('section', null, message), footer.length ? h('footer', null, footer) : null);
}

/** Re-sync a Basecoat component (tabs, dropdown menu) after its children were rebuilt. Basecoat's
 *  script attaches refresh() to the component's root; before it has run, there's nothing to sync. */
export function refreshComponent(el: Element) {
  (el as Element & { refresh?: () => void }).refresh?.();
}

/** Coalesce calls to one per animation frame. */
export function rafBatch(fn: () => void): () => void {
  let queued = false;
  return () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; fn(); });
  };
}

export function fmt(n: number): string { return n.toLocaleString('en-US'); }

export function downloadBlob(parts: BlobPart[], name: string, type: string) {
  const url = URL.createObjectURL(new Blob(parts, { type }));
  const a = h('a', { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { return false; }
}
